import {
  EPublishStatus,
  ERunStatus,
  getPublishStatusText,
  IRunRecordInfo,
  isPublishedStatus,
  RUN_STATUS_MAP,
  TaskStatisticsVo,
} from '@/constants/geo';
import { Button, Checkbox, Flex, Form, Input, message, Radio, Select, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import styles from './index.module.less';
import { timeFormatToYearSecond } from '@/utils/date';
import { useRequest, useSetState } from 'ahooks';
import { ERerunStrategy, ETaskSource, phraseApi, taskApi } from '@/axios/company';
import CommonModalFactory from '@/plugins/ModalFactory';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useStore } from '@/stores';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import SearchInput from '@/components/SearchInput';
import { EAnalysisStrategy, EAutoUpdateType } from '@/axios/spider';
import PlatFormItem from '@/components/Form/PlatformItem';
import PlatformItem from '@/components/Form/PlatformItem';
import { IPlatformItem } from '@/axios/common';
import AccessButton from '@/components/AccessButton';
import OperatorBtns from '@/components/Table/operateBtns';
import EllipsisText from '@/components/Table/EllipsisText';
import CommonTable from '@/components/Table';
import { openTemplateModal } from '../Detail';
import RunRecordResult from '../RunRecordResult';
import CustomTreeSelect from '@/components/Base/TreeSelect';
import useResetToFirstPageOrRefresh from '@/hooks/useResetToFirstPageOrRefresh';

type MonitorListParams = {
  pageIndex: number;
  pageSize: number;
  taskId: string;
  source: ETaskSource;
  search: string;
};
type MonitorListParamsUpdater = (params: Partial<MonitorListParams>) => void;

const RESULT_RUN_STATUSES = new Set<ERunStatus>([
  ERunStatus.Success,
  ERunStatus.Fail,
  ERunStatus.TIMEOUT,
  ERunStatus.STOPPED,
]);

const MonitorTable = forwardRef(
  (
    {
      from = 'company',
      btns,
      externalParams,
      onParamsChange,
      buildCompanyDetailUrl,
    }: {
      from?: 'all' | 'company';
      btns?: React.ReactNode;
      externalParams?: MonitorListParams;
      onParamsChange?: MonitorListParamsUpdater;
      buildCompanyDetailUrl?: (companyId: string | number) => string;
    },
    ref: any
  ) => {
    const id = new URLSearchParams(window.location.search).get('companyId');
    const appStore = useStore('app');
    const navigate = useNavigate();
    const [innerSearchParams, setInnerSearchParams] = useSetState<MonitorListParams>({
      pageIndex: 1,
      pageSize: 10,
      taskId: id || '',
      source: ETaskSource.Company,
      search: '',
    });
    const updateInnerSearchParams: MonitorListParamsUpdater = (params) => {
      setInnerSearchParams(params as Partial<MonitorListParams> & MonitorListParams);
    };
    const searchParams = externalParams || innerSearchParams;
    const setSearchParams = onParamsChange || updateInnerSearchParams;
    const {
      data: runRecordListInfo,
      loading,
      runAsync: refreshTaskList,
    } = useRequest(
      () => {
        return taskApi.list(searchParams);
      },
      {
        refreshDeps: [searchParams],
      }
    );
    const resetToFirstPageOrRefresh = useResetToFirstPageOrRefresh(searchParams, setSearchParams, refreshTaskList);
    const [selectedRecordIds, setSelectedRecordIds] = useState<string[]>([]);

    useEffect(() => {
      setSelectedRecordIds([]);
    }, [runRecordListInfo?.records]);

    const hasSelectedRecords = selectedRecordIds.length > 0;

    const columns: ColumnsType<IRunRecordInfo> = [
      ...(from === 'all'
        ? [
            {
              title: '企业',
              dataIndex: 'companyName',
              width: 200,
              ellipsis: true,
              render: (value: string, record: IRunRecordInfo) => (
                <a
                  onClick={() => {
                    navigate(buildCompanyDetailUrl?.(record.taskId) || `/tasks?companyId=${record.taskId}`);
                  }}
                >
                  <EllipsisText text={value} />
                </a>
              ),
            },
          ]
        : []),
      {
        title: '任务名称',
        dataIndex: 'taskName',
        ellipsis: true,
        width: 130,
        render: (value, record) => (
          <Tooltip
            title={
              <>
                {value}
                <br />
                {record.id}
              </>
            }
          >
            {value}
          </Tooltip>
        ),
      },
      {
        title: '运行状态',
        dataIndex: 'runStatus',
        width: 240,
        render: (value, record) => {
          if (!value) {
            return '';
          }
          const status = RUN_STATUS_MAP[value as ERunStatus] || value;
          const isSuccess = value === ERunStatus.Success;
          const isRunning = value === ERunStatus.Running;
          return (
            <div className="common_flex">
              <span>总数: {record?.total}，</span>
              {isSuccess ? (
                <span
                  className={styles['ml_10']}
                  style={{
                    color: isSuccess ? '#52C41A' : undefined,
                  }}
                >
                  成功: {record?.complete}，失败: {record?.fail}
                </span>
              ) : (
                <span
                  style={{
                    color: value === ERunStatus.Running ? '#1677ff' : '#F5222D',
                  }}
                >
                  {isRunning ? (
                    <Tag color="#36B274" variant="filled">
                      运行中
                    </Tag>
                  ) : (
                    <>
                      <CloseCircleOutlined /> {status}
                    </>
                  )}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: '曝光度(%)',
        dataIndex: 'exposure',
        width: 100,
        render: (val) => {
          return val + '%';
        },
      },
      {
        title: '搜索词组',
        dataIndex: 'componentName',
        width: 200,
        ellipsis: true,
        render: (value) => <EllipsisText text={value.join('、')} />,
      },
      {
        title: '开始时间',
        dataIndex: 'startTime',
        width: 120,
        render: (value) => (
          <div style={{ whiteSpace: 'pre-wrap' }}>{timeFormatToYearSecond(value).split(' ').join('\n')}</div>
        ),
      },
      {
        title: '完成时间',
        dataIndex: 'finishTime',
        width: 120,
        render: (value) => (
          <div style={{ whiteSpace: 'pre-wrap' }}>{timeFormatToYearSecond(value).split(' ').join('\n')}</div>
        ),
      },
      {
        title: '客户后台状态',
        dataIndex: 'publishStatusText',
        width: 120,
        render: (_, record) => getPublishStatusText(record.publishStatus),
      },

      {
        title: '操作',
        dataIndex: 'op',
        width: 250,
        render: (value, record) => {
          const isSuccess = record.runStatus === ERunStatus.Success;
          const isResult = RESULT_RUN_STATUSES.has(record.runStatus);

          return (
            <OperatorBtns
              btns={[
                {
                  type: 'common',
                  text: '查看结果',
                  onClick: () => {
                    openRunRecordResultModal({ record });
                  },
                  show: true,
                },
                {
                  type: 'common',
                  text: isPublishedStatus(record.publishStatus) ? '取消发布' : '发布',
                  onClick: async () => {
                    if (isPublishedStatus(record.publishStatus)) {
                      await taskApi.unpublish([record.id]);
                      message.success('取消发布成功');
                    } else {
                      await taskApi.publish([record.id]);
                      message.success('发布成功');
                    }
                    setSelectedRecordIds([]);
                    refreshTaskList();
                  },
                  show: from === 'company' && isResult,
                  permission: ['company:task:edit'],
                },
                {
                  type: 'common',
                  text: '扩展',
                  onClick: () => {
                    openExpandWordModal(
                      {
                        taskId: record.id,
                        companyId: from === 'company' ? id : record.taskId,
                        selectedExpandWords: record.expandWords?.map((item) => item.recordId),
                      },
                      undefined,
                      () => {
                        message.success('扩展成功');
                        refreshTaskList();
                      }
                    );
                  },
                  show: true,
                  permission: ['company:task:expand'],
                },
                {
                  type: 'common',
                  text: '查看企业',
                  onClick: () => {
                    navigate(buildCompanyDetailUrl?.(record.taskId) || `/tasks?companyId=${record.taskId}`);
                  },
                  show: from === 'all',
                  permission: ['company:view'],
                },
                {
                  type: 'common',
                  text: '查看报表',
                  onClick: async () => {
                    // window.open(`/geoReport/${record.id}?&source=${ETaskSource.Task}`);
                    openTemplateModal({ recordId: record.id, source: ETaskSource.Task }, undefined, undefined);
                  },
                  show: true,
                },

                {
                  type: 'common',
                  text: '重跑任务',
                  onClick: () => {
                    openRerunTaskModal(
                      {
                        recordId: record.id,
                        companyId: id || '',
                        platformList: appStore.platformList,
                        taskDetail: record,
                      },
                      undefined,
                      () => {
                        resetToFirstPageOrRefresh();
                      }
                    );
                  },
                  show: from === 'company' && isResult,
                  permission: ['company:task:create'],
                },
                {
                  type: 'common',
                  text: '导出任务',
                  onClick: () => {
                    const platforms = record.platformMappings.map((item) => item.platform);
                    openExportTaskModal({
                      platformList: appStore.platformList.filter((item) => platforms.includes(item.platform)),
                      recordId: record.id,
                    });
                  },
                  show: from === 'company' && isResult,
                  permission: ['company:task:export'],
                },
              ]}
            />
          );
        },
      },
    ];
    const rowSelection =
      from === 'company'
        ? {
            selectedRowKeys: selectedRecordIds,
            onChange: (keys: any[], _rows: IRunRecordInfo[]) => {
              setSelectedRecordIds(keys as string[]);
            },
            getCheckboxProps: (record: IRunRecordInfo) => ({
              disabled: record.runStatus !== ERunStatus.Success,
            }),
          }
        : undefined;

    useImperativeHandle(ref, () => ({
      refreshList: (stayCurrent = false) => {
        if (!stayCurrent) {
          resetToFirstPageOrRefresh();
        } else {
          refreshTaskList();
        }
      },
    }));
    return (
      <Flex vertical gap={20}>
        <Flex gap={8} align="center" justify="space-between">
          {from === 'company' && (
            <Flex gap={8} align="center">
              {btns}
              {hasSelectedRecords && (
                <AccessButton permission={['company:task:edit']}>
                  <Button
                    onClick={async () => {
                      await taskApi.batchPublish(selectedRecordIds);
                      message.success('批量发布成功');
                      setSelectedRecordIds([]);
                      refreshTaskList();
                    }}
                  >
                    批量发布
                  </Button>
                </AccessButton>
              )}
              {hasSelectedRecords && (
                <AccessButton permission={['company:task:edit']}>
                  <Button
                    onClick={async () => {
                      await taskApi.unpublish(selectedRecordIds);
                      message.success('批量取消发布成功');
                      setSelectedRecordIds([]);
                      refreshTaskList();
                    }}
                  >
                    批量取消发布
                  </Button>
                </AccessButton>
              )}

              {!hasSelectedRecords && (
                <AccessButton permission={['company:task:create']}>
                  <Button
                    type="primary"
                    onClick={() => {
                      openCreateMonitorTaskModal(
                        {
                          companyId: id || '',
                          platformList: appStore.platformList,
                        },
                        undefined,
                        () => {
                          resetToFirstPageOrRefresh();
                          message.success('创建成功');
                        }
                      );
                    }}
                  >
                    创建任务
                  </Button>
                </AccessButton>
              )}
            </Flex>
          )}
          <SearchInput
            style={{ width: '200px', marginLeft: 'auto' }}
            placeholder={from === 'company' ? '搜索任务名称' : '搜索任务名称/企业名称'}
            defaultValue={searchParams.search}
            onSearch={(val) => {
              setSearchParams({ search: val, pageIndex: 1 });
            }}
          />
        </Flex>

        <CommonTable<IRunRecordInfo>
          loading={loading}
          rowKey={'id'}
          columns={columns}
          dataSource={runRecordListInfo?.records || []}
          total={runRecordListInfo?.total}
          params={searchParams}
          setParams={setSearchParams}
          rowSelection={rowSelection}
        />
      </Flex>
    );
  }
);
export default MonitorTable;
export const openRunRecordResultModal = CommonModalFactory<{
  reportData?: TaskStatisticsVo['analysisResult'];
  record: IRunRecordInfo;
}>(
  {
    title: '查看运行结果',
    width: 1200,
    okButtonProps: {
      style: { display: 'none' },
    },
    cancelText: '关闭',
  },
  ({ data }) => (
    <RunRecordResult
      record={data?.record as IRunRecordInfo}
      reportData={data?.reportData as TaskStatisticsVo['analysisResult']}
    />
  ),

  async ({ record }) => {
    const res = await taskApi.getTaskDetail(record.id);
    return { reportData: res.analysisResult, record };
  }
);

export const openCreateMonitorTaskModal = CommonModalFactory<{
  companyId: string;
  platformList: IPlatformItem[];
  searchWordArray?: {
    label: string;
    value: string;
  }[];
  expandWordArray?: {
    label: string;
    value: string;
  }[];
}>(
  {
    title: '创建监控任务',
    width: 520,
    okText: '创建',
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        await taskApi.create({
          id: data?.companyId as string,
          source: ETaskSource.Company,
          platforms: values.platforms,
          componentWords: values.componentWords,
          expandWords: values.expandWords,
          analysisStrategy: values.analysisStrategy,
          autoAddMonitor: values.autoAddMonitor,
          autoUpdateBoard: values.autoUpdateBoard,
          taskName: values.taskName,
          publishToClient: values.publishToClient,
        });
      });
    }, [data?.companyId]);
    return (
      <div>
        <Form
          form={form}
          initialValues={{
            enableAnalysis: true,
            autoAddMonitor: false,
            autoUpdateBoard: EAutoUpdateType.None,
            // platforms: data?.platformList?.filter((item) => item.defaultCheck)?.map((item) => item.platform) || [],
            searchWords: [],
            analysisStrategy: EAnalysisStrategy.None,
            publishToClient: EPublishStatus.UNPUBLISHED,
          }}
          colon={false}
          layout="vertical"
        >
          <Form.Item name="taskName" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}>
            <Input placeholder="请输入任务名称" />
          </Form.Item>

          <PlatFormItem platformList={data?.platformList} />
          <Form.Item name="componentWords" label="搜索词组" rules={[{ required: true, message: '请选择搜索词组' }]}>
            {/* <Select options={data?.searchWordArray} mode="multiple" /> */}
            <CustomTreeSelect treeData={data?.searchWordArray} treeCheckable placeholder="请选择搜索词组" />
          </Form.Item>
          <Form.Item name="expandWords" label="扩展规则">
            {/* <Select options={data?.expandWordArray} mode="multiple" /> */}
            <CustomTreeSelect treeData={data?.expandWordArray} treeCheckable placeholder="请选择扩展规则" />
          </Form.Item>
          <Form.Item name="analysisStrategy" label="是否需要分析">
            <Radio.Group
              options={[
                {
                  value: EAnalysisStrategy.None,
                  label: '否',
                },
                {
                  value: EAnalysisStrategy.pre_optimization,
                  label: '优化前分析',
                },
                {
                  value: EAnalysisStrategy.post_optimization,
                  label: '优化后分析',
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="autoAddMonitor" label="添加到监控">
            <Radio.Group
              options={[
                {
                  value: true,
                  label: '是',
                },
                {
                  value: false,
                  label: '否',
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="autoUpdateBoard" label="是否自动更新至客户看板">
            <Radio.Group
              options={[
                {
                  value: EAutoUpdateType.None,
                  label: '不更新',
                },
                {
                  value: EAutoUpdateType.Increment,
                  label: '增量更新',
                },
                {
                  value: EAutoUpdateType.Overwrite,
                  label: '覆盖更新',
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="publishToClient" label="是否自动更新至客户后台">
            <Radio.Group
              options={[
                {
                  value: EPublishStatus.PUBLISHED,
                  label: '是',
                },
                {
                  value: EPublishStatus.UNPUBLISHED,
                  label: '否',
                },
              ]}
            />
          </Form.Item>
        </Form>
      </div>
    );
  },
  async ({ companyId, platformList }) => {
    const res = await phraseApi.list({
      pageIndex: 1,
      pageSize: 100,
      companyId,
      type: 'SEARCH_WORD',
    });
    const expandList = await phraseApi.list({
      pageIndex: 1,
      pageSize: 100,
      companyId,
      type: 'EXPAND_WORD',
    });
    return {
      companyId,
      platformList,
      searchWordArray: (res.records || []).map((item) => ({
        label: item.name as string,
        value: item.recordId as string,
      })),
      expandWordArray: (expandList.records || []).map((item) => ({
        label: item.name as string,
        value: item.recordId as string,
      })),
    };
  }
);
export const openRerunTaskModal = CommonModalFactory<{
  recordId: string;
  companyId: string;
  platformList: IPlatformItem[];
  taskDetail?: IRunRecordInfo;
}>(
  {
    title: '重跑任务',
    width: 400,
    okText: '重跑',
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        await taskApi.rerun({
          taskId: data?.recordId as string,
          platforms: values.platforms,
          strategy: values.strategy,
        });
        message.success('任务已重跑');
      });
    }, [data?.companyId, data?.recordId]);
    return (
      <Form
        form={form}
        initialValues={{
          platforms: data?.taskDetail?.platformMappings?.map((item) => item.platform) || [],
          strategy: ERerunStrategy.All,
        }}
      >
        <Form.Item name="taskName" label="任务名称">
          {data?.taskDetail?.taskName}
        </Form.Item>
        <PlatformItem platformList={data?.platformList} />
        <Form.Item name="strategy" label="重跑搜索词">
          <Radio.Group
            options={[
              { label: '失败', value: ERerunStrategy.Fail },
              { label: '不可见', value: ERerunStrategy.Invisible },
              { label: '全部', value: ERerunStrategy.All },
            ]}
          />
        </Form.Item>
      </Form>
    );
  }
);

export const openExportTaskModal = CommonModalFactory<{
  platformList: IPlatformItem[];
  recordId: string;
}>(
  {
    title: '导出任务',
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        // 不再提交 sentiment，导出明细仅按其余表单条件筛选。
        const downloadKey = await taskApi.download({
          recordIds: [data?.recordId],
          ...values,
        });
        window.open(`/api/common/download/${downloadKey}`, '_blank');
      });
    }, []);
    return (
      <Form
        form={form}
        initialValues={{
          platforms: data?.platformList?.map((item) => item.platform) || [],
          status: [ERunStatus.Success],
          visibility: [true, false],
        }}
      >
        <PlatformItem platformList={data?.platformList} />
        <Form.Item name="status" label="任务状态">
          <Checkbox.Group
            options={[
              { label: '运行中', value: ERunStatus.Running },
              { label: '已完成', value: ERunStatus.Success },
              { label: '失败', value: ERunStatus.Fail },
            ]}
          />
        </Form.Item>
        <Form.Item name="visibility" label="可见性">
          <Checkbox.Group
            options={[
              { label: '可见', value: true },
              { label: '不可见', value: false },
            ]}
          />
        </Form.Item>
      </Form>
    );
  }
);
export const openExpandWordModal = CommonModalFactory<{
  taskId: string;
  expandWords: { label: string; value: string }[];
  selectedExpandWords?: string[];
}>(
  {
    title: '扩展',
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        await taskApi.updateTaskExpand({
          taskId: data?.taskId as string,
          expandWords: values.expandWords,
        });
      });
    }, [data, form]);
    return (
      <Form
        form={form}
        initialValues={{
          expandWords: data?.selectedExpandWords || [],
        }}
      >
        <Form.Item name="expandWords" label="扩展规则">
          <Select options={data?.expandWords} mode="multiple" />
        </Form.Item>
      </Form>
    );
  },
  async ({ companyId, ...props }) => {
    const expandWords = await phraseApi.list({
      companyId,
      pageIndex: 1,
      pageSize: 100,
      type: 'EXPAND_WORD',
    });
    return {
      ...props,
      expandWords: (expandWords.records || []).map((item) => ({
        label: item.name as string,
        value: item.recordId as string,
      })),
    };
  }
);
